const GOAT_IMAGE_HOST = "image.goat.com";
const IMAGE_PROXY_URL = "/api/product-storage/image-proxy";

export interface BuildImageUrlOptions {
  /** Целевая ширина в px (используется и для goat transform, и для x-oss-process). */
  width: number;
  /** Целевая высота в px. Для goat игнорируется — он режет только по width. */
  height?: number;
  /** Формат для x-oss-process (poizon/dewu CDN). */
  format?: "webp" | "jpg" | "png";
  /** Добавлять ли m_pad в x-oss-process. */
  pad?: boolean;
}

const viaImageProxy = (url: string): string =>
  `${IMAGE_PROXY_URL}?url=${encodeURIComponent(url)}`;

/**
 * Строит URL миниатюры с учётом источника изображения.
 *
 * goat игнорирует x-oss-process и отдаёт тяжёлый оригинал, который душит
 * троттлинг на маршруте → картинка не грузится. Для goat используем нативный
 * transform-эндпоинт и гоним результат через image-proxy (обход троттлинга +
 * серверный кэш). Для poizon/dewu CDN x-oss-process работает штатно.
 */
export const buildImageUrl = (
  rawUrl: string,
  { width, height, format, pad = true }: BuildImageUrlOptions,
): string => {
  if (!rawUrl) {
    return rawUrl;
  }

  let parsed: URL | undefined;
  try {
    parsed = new URL(rawUrl);
  } catch {
    parsed = undefined;
  }

  if (parsed?.hostname === GOAT_IMAGE_HOST) {
    const attachmentsIndex = parsed.pathname.indexOf("/attachments/");
    if (attachmentsIndex !== -1) {
      const path = parsed.pathname.slice(attachmentsIndex + 1);
      return viaImageProxy(
        `https://${GOAT_IMAGE_HOST}/transform/v1/${path}?action=crop&width=${width}`,
      );
    }
    // goat-URL без transform-пути всё равно гоним через прокси: серверного
    // ресайза не будет, но это обходит троттлинг и оригинал догрузится.
    return viaImageProxy(rawUrl);
  }

  const resize = [height && `h_${height}`, `w_${width}`, pad && "m_pad"]
    .filter(Boolean)
    .join(",");
  const formatPart = format ? `/format,${format}` : "";

  return `${rawUrl}?x-oss-process=image/resize,${resize}${formatPart}`;
};
